import { Location } from '@angular/common';
import { Component, Inject, OnInit, Optional } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { ToastService } from 'src/app/core';
import { ConfigurationApi, DocumentConfigFilter, DocumentConfigs, DocumentTypes, GovernanceActivities, LookupApi, LookupField, OfficeEntity } from 'src/app/core/api.gen';
import { LoaderService } from 'src/app/core/loader.service';
import { LookupTypeKeys } from 'src/app/shared/lookup-type-keys';

@Component({
  selector: 'eos-governance-activities-edit',
  templateUrl: './governance-activities-edit.component.html',
  styleUrls: ['./governance-activities-edit.component.css']
})
export class GovernanceActivitiesEditComponent implements OnInit {
  isEdit = false;
  activity: GovernanceActivities;

  countries = [];
  legalEntities: OfficeEntity[] = [];
  officeLocations: LookupField[] = [];
  filteredLegalEntities: OfficeEntity[] = [];
  filteredOfficeLocations: LookupField[] = [];
  documentTypes: DocumentTypes[] = [];
  allDocConfigs: DocumentConfigs[] = [];
  filteredDocumentTypes: DocumentTypes[] = [];
  missingLEsForDocType: OfficeEntity[] = [];

  activityTypes = [
    { id: 100, displayText: 'Email' },
    { id: 101, displayText: 'Profile update' },
    { id: 102, displayText: 'External' },
    { id: 104, displayText: 'Document upload' },
  ];

  createForm = new UntypedFormGroup({
    id: new UntypedFormControl(0),
    activityType: new UntypedFormControl(null, [Validators.required]),
    order: new UntypedFormControl(null, [Validators.required]),
    header: new UntypedFormControl(null, [Validators.required]),
    description: new UntypedFormControl(null, [Validators.required]),
    isActive: new UntypedFormControl(true, [Validators.required]),
    isCommon: new UntypedFormControl(false),
    country: new UntypedFormControl(null, [Validators.required]),
    officeLocation: new UntypedFormControl(null, [Validators.required]),
    legalEntity: new UntypedFormControl(null, [Validators.required]),
    documentType: new UntypedFormControl(null, [Validators.required]),
    documentConfigId: new UntypedFormControl(null),
  });

  private readonly scopeControls = ['country', 'officeLocation', 'legalEntity', 'documentType'];


  constructor(
    private lookupApi: LookupApi,
    private loader: LoaderService,
    private toast: ToastService,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private configApi: ConfigurationApi,
  ) { }

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    const id = idParam ? +idParam : null;
    this.isEdit = !!id;

    this.loader.show();
    forkJoin({
      countries: this.lookupApi.getLookupFieldsByTypes([LookupTypeKeys.OFFICE_LOCATION_COUNTRY]),
      entities: this.lookupApi.getOfficeEntities(undefined, undefined),
      officeLocations: this.lookupApi.getLookupFieldsByTypes([LookupTypeKeys.OFFICE_LOCATION]),
      documentTypes: this.configApi.getDocumentTypes(),
      docConfigs: this.configApi.getDocumentConfigs(new DocumentConfigFilter()),
      activity: id ? this.configApi.getGovernanceActivitiesById(id) : of(null),
    })
    .pipe(finalize(() => this.loader.clear()))
    .subscribe(({ countries, entities, officeLocations, documentTypes, docConfigs, activity }) => {
      this.countries = countries;
      this.legalEntities = entities;
      this.officeLocations = officeLocations;
      this.documentTypes = documentTypes;
      this.allDocConfigs = docConfigs;

      if (this.isEdit && activity) {
        this.activity = activity;

        const isCommonActivity = !this.activity.documentConfigId && !this.activity.documentConfig;
        if (isCommonActivity) {
          this.createForm.patchValue({
            id: this.activity.id,
            activityType: this.activity.activityType,
            order: this.activity.order,
            header: this.activity.header,
            description: this.activity.description,
            isActive: this.activity.isActive,
            isCommon: true,
            documentConfigId: null,
          });
          this.applyCommonState(true);
          return;
        }

        const countryId  = this.activity.documentConfig?.countryId;
        const locationId = this.activity.documentConfig?.locationId;
        const leId       = this.activity.documentConfig?.legalEntityId;

        const selectedCountry = countryId
          ? this.countries.find(c => c.id === countryId) ?? null
          : null;

        if (selectedCountry) {
          this.filteredOfficeLocations = this.officeLocations.filter(l => l.parentFieldId === selectedCountry.id);
          this.filteredLegalEntities = this.legalEntities.filter(e => e.parentFieldId === selectedCountry.id);
        }

        const selectedLocation = locationId
          ? this.filteredOfficeLocations.find(l => l.id === locationId) ?? null
          : null;

        const selectedLE = leId
          ? this.filteredLegalEntities.find(e => e.id === leId) ?? null
          : null;

        this.filterDocumentTypes(countryId, locationId, leId ? [leId] : []);

        const documentTypeId = this.activity.documentConfig?.documentTypeId
          ?? this.allDocConfigs.find(c => c.id === this.activity.documentConfigId)?.documentTypeId;

        const selectedDocType = documentTypeId
          ? this.filteredDocumentTypes.find(d => d.id === documentTypeId) ?? null
          : null;

        const resolvedConfigId = this.resolveDocConfigId(
          countryId,
          locationId,
          leId,
          documentTypeId
        );

        this.createForm.patchValue({
          id: this.activity.id,
          activityType: this.activity.activityType,
          order: this.activity.order,
          header: this.activity.header,
          description: this.activity.description,
          isActive: this.activity.isActive,
          country: selectedCountry,
          officeLocation: selectedLocation,
          legalEntity: selectedLE ? [selectedLE] : [],
          documentType: selectedDocType,
          documentConfigId: resolvedConfigId,
        });
      }
    });
  }

  filterDocumentTypes(countryId?: number, locationId?: number, legalEntityIds?: number[]) {
    if (!countryId || !locationId || !legalEntityIds?.length) {
      this.filteredDocumentTypes = [];
      return;
    }
    const configs = this.allDocConfigs.filter(c =>
      c.countryId === countryId &&
      c.locationId === locationId &&
      legalEntityIds.includes(c.legalEntityId)
    );
    const docTypeIds = new Set(configs.map(c => c.documentTypeId));
    this.filteredDocumentTypes = this.documentTypes.filter(d => docTypeIds.has(d.id));

    const current = this.createForm.controls.documentType.value;
    if (current && !docTypeIds.has(current.id)) {
      this.createForm.controls.documentType.reset();
      this.createForm.controls.documentConfigId.setValue(null);
    }
  }

  private resolveDocConfigId(countryId?: number, locationId?: number, legalEntityId?: number, documentTypeId?: number): number | null {
    if (!documentTypeId) return null;
    let configs = this.allDocConfigs.filter(c => c.documentTypeId === documentTypeId);
    if (countryId) configs = configs.filter(c => c.countryId === countryId);
    if (locationId) configs = configs.filter(c => c.locationId === locationId);
    if (legalEntityId) configs = configs.filter(c => c.legalEntityId === legalEntityId);
    return configs[0]?.id ?? null;
  }

  onCountryChange(event: any) {
    this.createForm.controls.officeLocation.reset();
    this.createForm.controls.legalEntity.reset();
    this.createForm.controls.documentType.reset();
    this.createForm.controls.documentConfigId.setValue(null);
    this.filteredOfficeLocations = event
      ? this.officeLocations.filter(l => l.parentFieldId === event.id)
      : [];
    this.filteredLegalEntities = event
      ? this.legalEntities.filter(e => e.parentFieldId === event.id)
      : [];
    this.filteredDocumentTypes = [];
  }

  onOfficeLocationChange(event: any) {
    this.createForm.controls.documentType.reset();
    this.createForm.controls.documentConfigId.setValue(null);
    const countryId = this.createForm.controls.country.value?.id;
    const selectedLEs = this.createForm.controls.legalEntity.value;
    const leIds: number[] = Array.isArray(selectedLEs) ? selectedLEs.map(e => e.id) : (selectedLEs ? [selectedLEs.id] : []);
    this.filterDocumentTypes(countryId, event?.id, leIds);
  }

  onLegalEntityChange(event: any) {
    this.createForm.controls.documentType.reset();
    this.createForm.controls.documentConfigId.setValue(null);
    this.missingLEsForDocType = [];
    const countryId = this.createForm.controls.country.value?.id;
    const locationId = this.createForm.controls.officeLocation.value?.id;
    const leIds: number[] = Array.isArray(event) ? event.map(e => e.id) : (event ? [event.id] : []);
    this.filterDocumentTypes(countryId, locationId, leIds);
  }

  onDocumentTypeChange(event: any) {
    const countryId = this.createForm.controls.country.value?.id;
    const locationId = this.createForm.controls.officeLocation.value?.id;
    const selectedLEs: any[] = Array.isArray(this.createForm.controls.legalEntity.value)
      ? this.createForm.controls.legalEntity.value
      : (this.createForm.controls.legalEntity.value ? [this.createForm.controls.legalEntity.value] : []);

    this.missingLEsForDocType = selectedLEs.filter(le =>
      !this.resolveDocConfigId(countryId, locationId, le.id, event?.id)
    );
  }

  onCommonChange(checked: boolean) {
    this.applyCommonState(checked);
  }

  private applyCommonState(isCommon: boolean) {
    this.scopeControls.forEach(name => {
      const ctrl = this.createForm.controls[name];
      if (isCommon) {
        ctrl.reset();
        ctrl.clearValidators();
        ctrl.disable();
      } else {
        ctrl.enable();
        ctrl.setValidators([Validators.required]);
      }
      ctrl.updateValueAndValidity();
    });

    if (isCommon) {
      this.createForm.controls.documentConfigId.setValue(null);
      this.filteredOfficeLocations = [];
      this.filteredLegalEntities = [];
      this.filteredDocumentTypes = [];
      this.missingLEsForDocType = [];
    }
  }

  saveActivity() {
    if (!this.createForm.valid) return;
    const raw = this.createForm.getRawValue();
    if (raw.isActive && raw.order <= 0) {
      this.toast.warning('Order should be greater than zero for an active activity');
      return;
    }

    let payloads: GovernanceActivities[];

    if (raw.isCommon) {
      // A common activity has no document config; persist a single null-config row.
      payloads = [GovernanceActivities.fromJS({
        id: raw.id ?? 0,
        activityType: raw.activityType,
        order: raw.order,
        header: raw.header,
        description: raw.description,
        isActive: raw.isActive,
        documentConfigId: null,
      })];
    } else {
      const countryId = raw.country?.id;
      const locationId = raw.officeLocation?.id;
      const documentTypeId = raw.documentType?.id;
      const selectedLEs: any[] = Array.isArray(raw.legalEntity) ? raw.legalEntity : (raw.legalEntity ? [raw.legalEntity] : []);

      payloads = selectedLEs.map((le, index) => {
        const configId = this.resolveDocConfigId(countryId, locationId, le.id, documentTypeId);
        return GovernanceActivities.fromJS({
          id: this.isEdit && index === 0 ? raw.id : 0,
          activityType: raw.activityType,
          order: raw.order,
          header: raw.header,
          description: raw.description,
          isActive: raw.isActive,
          documentConfigId: configId ?? null,
        });
      });

      if (!payloads.length || payloads.some(p => !p.documentConfigId)) {
        this.toast.warning('No document configuration found for one or more selected legal entities');
        return;
      }
    }

    this.loader.show();
    forkJoin(payloads.map(p => this.configApi.saveGovernanceActivities(p)))
      .pipe(finalize(() => this.loader.clear()))
      .subscribe(
        resps => {
          if (resps.some(r => r?.statusCode === 400)) {
            this.toast.warning('Activity with the same order already exists');
          } else {
            this.toast.success(this.isEdit ? 'Activity successfully updated' : 'Activity successfully created');
            this.location.back();
          }
        },
        () => this.toast.error('An error occurred. Please try again.')
      );
  }

  onCancel() {
    this.location.back();
  }
}